import React, { useMemo } from "react";

import { Select } from "components/forms";
import { debtIndexNames } from "constants/debt";
import { FormikProps } from "formik";
import { useTranslation } from "react-i18next";

import { FormValues } from "../schemas";

interface Props {
  name: "interest_reference_index" | "principal_reference_index" | "pik_reference_index";
  formik: FormikProps<FormValues>;
  onIndexChange?: (index: string) => void;
  onIndexClear?: () => void;
  label?: string;
  disabled?: boolean;
  size?: "small" | "medium";
}

export const ReferenceIndexSelect: React.FC<Props> = ({
  name,
  formik,
  onIndexChange,
  onIndexClear,
  label,
  disabled = false,
  size
}) => {
  const { t } = useTranslation("debt");

  const referenceIndexOptions = useMemo(
    () => [
      { value: "none", label: t("linkage.unlinked") },
      ...Object.entries(debtIndexNames).map(([value, defaultLabel]) => ({
        value,
        label: t(defaultLabel, { defaultValue: defaultLabel })
      }))
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  return (
    <Select
      disabled={disabled}
      name={name}
      label={label ?? t("pikSection.linkage")}
      value={formik.values[name] || "none"}
      variant="outlined"
      fullWidth
      size={size}
      items={referenceIndexOptions.map((option) => ({
        key: option.value,
        value: option.value,
        text: option.label
      }))}
      error={formik.touched[name] && Boolean(formik.errors[name])}
      helperText={formik.touched[name] ? (formik.errors[name] as string) : undefined}
      onChange={(e) => {
        const selectedValue = e.target.value as string;
        const formikValue = selectedValue === "none" ? null : selectedValue;
        formik.setFieldValue(name, formikValue);

        if (formikValue === null) {
          onIndexClear?.();
        } else {
          onIndexChange?.(formikValue);
        }
      }}
    />
  );
};
